// ---------------------------------------------------------------------------
// Small interface texts that used to be hard-coded in English: accessibility
// labels, the two text illustrations of the market-simulation page, the
// contact page labels, the MS Trader architecture diagram.
// EN and DE are maintained here; the other languages are translations of the
// English block in locales/<code>/ui.json (type-checked against it).
// ---------------------------------------------------------------------------
import { Lang } from "config/i18n/languages";
import frCopy from "./locales/fr/ui.json";
import esCopy from "./locales/es/ui.json";
import itCopy from "./locales/it/ui.json";
import plCopy from "./locales/pl/ui.json";
import zhCopy from "./locales/zh/ui.json";
import jaCopy from "./locales/ja/ui.json";
import koCopy from "./locales/ko/ui.json";

const en = {
  aria: {
    home: "MARKSIM – home",
    menu: "Menu",
    footer: "Footer",
    language: "Language",
  },
  art: {
    gap: ["Bridging", "the Gap"],
    ml: ["Machine Learning", "for", "Better Forecasts"],
  },
  contact: {
    company: "Company name",
    and: "and",
    register: "Commercial register",
    vat: "VAT ID",
    gln: "GLN",
    phone: "Phone",
    email: "E-mail",
    court: "Court of jurisdiction",
    courtValue: "Commercial Court of Vienna (Handelsgericht Wien)",
    details: "Detailed company data:",
    wko: "Austrian Economic Chamber company directory (WKO)",
  },
  imprintNote:
    "The legal notice and terms are available in English and German; the English text applies.",
  planInfo: "More information",
  deskAlt: "MS Trader – trading desk",
  diagram: {
    cluster: "Kubernetes cluster · customer site",
    trader: "Trader",
    browser: "browser",
    mslogin: "auth · spawns gateway pod",
    gateway: [
      "one pod per trader",
      "REST · WebSocket",
      "own FIX trading session",
    ],
    mstrader: "PostgreSQL · private data",
    perExchange: "one per exchange",
    fanout: "market-data fan-out",
    writesHistory: "writes history",
    msmarketdata: "TimescaleDB · public data",
    exchange: "Exchange",
    exchangeLines: ["any FIX 4.4 venue", "one connector set per exchange"],
    marketData: "Market data",
    fixLines: "FIX 4.4 lines",
    trading: "Trading",
    fixLine: "FIX 4.4 line",
    login: "login: password + TOTP",
    spawn: "spawns pod · issues token",
    restWs: "REST + WebSocket",
    tradingSession: "FIX 4.4 trading session · D / F / G / 8 / 9 …",
    mdSession: "MD session",
    md: "MD",
    mdFeed: ["FIX 4.4 market data", "fan-out to all gateways"],
    orders: ["orders · executions", "FIX sequence numbers"],
    ticks: "ticks · trades · news",
    history: ["history · catalogue", "(read-only)"],
    tunnel: "VPN tunnel · TLS",
  },
  insights: {
    title: ["MARKSIM", "in practice"],
    lead: "From the choice task to the market-share forecast: what a MARKSIM study delivers, step by step.",
    note: "Illustrative example data",
    steps: [
      {
        kicker: "Survey",
        title: "Choices instead of rating scales",
        text: "Respondents choose between realistic product profiles. MARKSIM varies the features systematically and generates a randomised questionnaire for every respondent.",
      },
      {
        kicker: "Utilities",
        title: "What really drives the decision",
        text: "Hierarchical Bayes estimation shows how much each feature contributes to the choice – for every respondent and every segment.",
      },
      {
        kicker: "Market map",
        title: "Segments and positioning on one map",
        text: "Multidimensional scaling places respondents, products and features on one map; cluster analysis forms need-based segments.",
      },
      {
        kicker: "Simulation",
        title: "Market shares for every segment",
        text: "The simulator forecasts market shares for any product scenario – in total and for each segment.",
      },
    ],
    choice: {
      progress: "Task 3 of 12",
      question: "Which offer would you choose?",
      choose: "Choose",
    },
    product: "Product",
    attrs: {
      price: "Price",
      battery: "Battery life",
      camera: "Camera",
      storage: "Storage",
      display: "Display",
      brand: "Brand",
    },
    importance: "Relative importance",
    map: {
      priceSensitive: "price-sensitive",
      priceSecondary: "price is secondary",
    },
    segment: "Segment",
    total: "Total",
  },
};

export type UiCopy = typeof en;

const de: UiCopy = {
  aria: {
    home: "MARKSIM – Startseite",
    menu: "Menü",
    footer: "Fußzeile",
    language: "Sprache",
  },
  art: {
    gap: ["Die Lücke", "schließen"],
    ml: ["Machine Learning", "für", "bessere Prognosen"],
  },
  contact: {
    company: "Firmenname",
    and: "und",
    register: "Firmenbuch",
    vat: "UID-Nummer",
    gln: "GLN",
    phone: "Telefon",
    email: "E-Mail",
    court: "Gerichtsstand",
    courtValue: "Handelsgericht Wien",
    details: "Ausführliche Firmendaten:",
    wko: "Firmen A–Z der Wirtschaftskammer Österreich (WKO)",
  },
  imprintNote: "",
  planInfo: "Mehr Informationen",
  deskAlt: "MS Trader – Handelsoberfläche",
  diagram: {
    cluster: "Kubernetes-Cluster · beim Kunden",
    trader: "Trader",
    browser: "Browser",
    mslogin: "Auth · startet Gateway-Pod",
    gateway: [
      "ein Pod pro Trader",
      "REST · WebSocket",
      "eigene FIX-Trading-Session",
    ],
    mstrader: "PostgreSQL · private Daten",
    perExchange: "einer pro Börse",
    fanout: "Marktdaten-Verteilung",
    writesHistory: "schreibt Historie",
    msmarketdata: "TimescaleDB · öffentliche Daten",
    exchange: "Börse",
    exchangeLines: [
      "jeder FIX-4.4-Handelsplatz",
      "ein Anschluss-Set pro Börse",
    ],
    marketData: "Marktdaten",
    fixLines: "FIX-4.4-Leitungen",
    trading: "Handel",
    fixLine: "FIX-4.4-Leitung",
    login: "Login: Passwort + TOTP",
    spawn: "startet Pod · stellt Token aus",
    restWs: "REST + WebSocket",
    tradingSession: "FIX-4.4-Trading-Session · D / F / G / 8 / 9 …",
    mdSession: "MD-Session",
    md: "MD",
    mdFeed: ["FIX-4.4-Marktdaten", "an alle Gateways verteilt"],
    orders: ["Orders · Ausführungen", "FIX-Sequenznummern"],
    ticks: "Ticks · Trades · News",
    history: ["Historie · Katalog", "(nur lesend)"],
    tunnel: "VPN-Tunnel · TLS",
  },
  insights: {
    title: ["MARKSIM", "in der Praxis"],
    lead: "Von der Wahlaufgabe bis zur Marktanteilsprognose: was eine MARKSIM-Studie Schritt für Schritt liefert.",
    note: "Beispieldaten zur Veranschaulichung",
    steps: [
      {
        kicker: "Befragung",
        title: "Entscheidungen statt Bewertungsskalen",
        text: "Die Befragten wählen zwischen realistischen Produktprofilen. MARKSIM variiert die Merkmale systematisch und erzeugt für jede Person einen eigenen, randomisierten Fragebogen.",
      },
      {
        kicker: "Nutzenwerte",
        title: "Was die Entscheidung wirklich treibt",
        text: "Die hierarchische Bayes-Schätzung zeigt, wie stark jedes Merkmal zur Wahl beiträgt – für jede befragte Person und jedes Segment.",
      },
      {
        kicker: "Marktkarte",
        title: "Segmente und Positionierung auf einer Karte",
        text: "Multidimensionale Skalierung bringt Befragte, Produkte und Merkmale auf eine Karte; die Clusteranalyse bildet bedarfsorientierte Segmente.",
      },
      {
        kicker: "Simulation",
        title: "Marktanteile für jedes Segment",
        text: "Der Simulator prognostiziert Marktanteile für beliebige Produktszenarien – gesamt und für jedes Segment.",
      },
    ],
    choice: {
      progress: "Aufgabe 3 von 12",
      question: "Welches Angebot würden Sie wählen?",
      choose: "Wählen",
    },
    product: "Produkt",
    attrs: {
      price: "Preis",
      battery: "Akkulaufzeit",
      camera: "Kamera",
      storage: "Speicher",
      display: "Display",
      brand: "Marke",
    },
    importance: "Relative Wichtigkeit",
    map: {
      priceSensitive: "preissensibel",
      priceSecondary: "Preis zweitrangig",
    },
    segment: "Segment",
    total: "Gesamt",
  },
};

const ui: Record<Lang, UiCopy> = {
  en,
  de,
  fr: frCopy,
  es: esCopy,
  it: itCopy,
  pl: plCopy,
  zh: zhCopy,
  ja: jaCopy,
  ko: koCopy,
};

export const uiEn = en;
export default ui;
